import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { usePagination } from '../../hooks/usePagination';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ShoppingCart, Search, Package, Truck, X, ExternalLink, Plus, Trash2, Loader2 } from 'lucide-react';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { EntityLink } from '../../components/shared/EntityLink';
import { PurchaseTracker } from '../../components/shared/PurchaseTracker';
import { Pagination } from '../../components/shared/Pagination';
import { SuppliersPanel } from '../suppliers/index';
import OrphansTab from './OrphansTab';
import InternalStockTab from './InternalStockTab';
import InternalMovementsTab from './InternalMovementsTab';
import { useCan, visibleTabs, pickActiveTab, Can, NoAccess, TAB_CAP } from '../../lib/rbac';

type Tab = 'amazon' | 'pos' | 'internal-pos' | 'internal-stock' | 'internal-movements' | 'orphans' | 'suppliers';

// Orden de las pestañas (los botones de arriba). Sirve para caer a la primera
// permitida cuando la elegida no lo está; las capacidades salen de TAB_CAP.purchases.
const TAB_ORDER: { key: Tab }[] = [
  { key: 'amazon' }, { key: 'pos' }, { key: 'internal-pos' }, { key: 'internal-stock' },
  { key: 'internal-movements' }, { key: 'orphans' }, { key: 'suppliers' },
];

const AMZ_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  ordered: 'info',
  shipped: 'warning',
  delivered: 'ok',
  delivered_ml: 'ok',
  pending_approval: 'warning',
  cancelled: 'error',
  error: 'error',
  failed: 'error',
  unlinked: 'unknown',
  from_stock: 'ok',
  safeguard_hold: 'warning',
};

const PO_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  draft: 'unknown',
  confirmed: 'info',
  partial_received: 'warning',
  received: 'ok',
  cancelled: 'error',
};

function AmazonPurchaseCard({ p, fmt, fmtDate, sellerId, onClick }: { p: any; fmt: (v: any) => string; fmtDate: (v: string | null) => string; sellerId: number; onClick: () => void }) {
  return (
    <div onClick={onClick} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 space-y-2 cursor-pointer active:bg-[var(--color-surface-hover)]">
      <div className="flex gap-2.5">
        <div className="w-10 h-10 rounded overflow-hidden bg-[var(--color-border-light)] shrink-0">
          {p.product_image ? <img src={p.product_image} alt="" className="w-full h-full object-contain" /> :
            <Package size={16} className="w-full h-full p-2.5 text-[var(--color-text-muted)]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-medium text-[var(--color-text)] truncate">{p.product_title || p.asin}</span>
            <span className="text-sm font-mono text-[var(--color-text)] shrink-0">{fmt(p.total_cost)}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-[var(--color-text-muted)]">
            <span className="font-mono">{p.asin}</span>
            <span>·</span>
            <span>{fmtDate(p.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={AMZ_STATUS_MAP[p.amazon_status] || 'unknown'} label={p.amazon_status} size="sm" />
        {p.ml_order_id && <span className="text-[10px] text-[var(--color-primary)] font-mono">#{p.ml_order_id}</span>}
        {p.tracking_number && <span className="text-[10px] font-mono text-[var(--color-text-muted)] truncate max-w-[100px]">{p.tracking_number}</span>}
        {p.gross_profit != null && (
          <span className={`text-xs font-mono ml-auto ${(p.gross_profit ?? 0) >= 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>
            {fmt(p.gross_profit)}
          </span>
        )}
      </div>
    </div>
  );
}

export default function PurchasesPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Read initial tab from URL query param (e.g., /purchases?tab=suppliers)
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    // Backwards-compat: legacy 'internal' (was internal-POs) → 'internal-pos'
    if (t === 'internal') return 'internal-pos' as Tab;
    if (t && ['amazon', 'pos', 'internal-pos', 'internal-stock', 'internal-movements', 'orphans', 'suppliers'].includes(t)) return t as Tab;
    return 'amazon' as Tab;
  })();
  const can = useCan();
  const [selectedTab, setSelectedTab] = useState<Tab>(initialTab);
  // Pestaña efectiva: la elegida si está permitida; si no, la primera permitida.
  const allowedTabs = visibleTabs('purchases', TAB_ORDER, can);
  const firstAllowed = pickActiveTab('purchases', TAB_ORDER, selectedTab, can) as Tab | null;
  const tab: Tab = firstAllowed ?? selectedTab;
  useEffect(() => {
    if (firstAllowed && firstAllowed !== selectedTab) setSelectedTab(firstAllowed);
  }, [firstAllowed, selectedTab]);
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('purchases');
  const { sellerId } = useUIStore();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadData = useCallback(async () => {
    if (tab === 'suppliers' || tab === 'orphans' || tab === 'internal-stock' || tab === 'internal-movements') { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({
      seller_id: String(sellerId),
      page: String(pag.page),
      per_page: String(pag.perPage),
    });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (search) params.set('search', search);

    if (tab === 'amazon') {
      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/purchases/amazon?${params}`),
        api.get<any>(`/purchases/amazon/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } else if (tab === 'pos') {
      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/purchases/pos?${params}`),
        api.get<any>(`/purchases/pos/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } else if (tab === 'internal-pos') {
      const [dataRes, statsRes] = await Promise.all([
        api.get<any>(`/internal-stock/purchases?${params}`),
        api.get<any>(`/internal-stock/purchases/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(Array.isArray(dataRes.data) ? dataRes.data : []);
        pag.setTotal((dataRes as any).total ?? 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    }
    setLoading(false);
  }, [tab, sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { pag.resetPage(); }, [sellerId]);

  // SSE real-time
  const { subscribe } = useSSEStore();
  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('stock:po-created', refresh),
      subscribe('stock:po-confirmed', refresh),
      subscribe('stock:po-received', refresh),
      subscribe('stock:po-cancelled', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const handleSearch = (val: string) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => { setSearch(val); pag.resetPage(); }, 400);
    setSearchTimeout(timeout);
  };

  const switchTab = (newTab: Tab) => {
    setSelectedTab(newTab);
    pag.resetPage();
    setStatusFilter('all');
    setSearch('');
    setStats(null);
  };

  // Sin la llave del dinero los importes se muestran como "—" (misma cifra, tapada).
  const canMoney = can('global.view_money');
  const fmt = (v: any) => (canMoney && v != null ? `$${Number(v).toFixed(2)}` : '—');
  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleDateString() : '—';

  if (allowedTabs.length === 0) return <NoAccess />;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('purchases.title')}</h1>
        {/* Crear una OC carga costos: pide operar Y ver dinero. */}
        {(tab === 'amazon' || tab === 'pos' || tab === 'internal-pos') && canMoney && (
          <Can cap="purchases.operate_pos">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors">
                <Plus size={16} />
                {t('purchases.newPO')}
              </button>
            </div>
          </Can>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-1 w-fit overflow-x-auto">
        <Can cap={TAB_CAP.purchases.amazon}>
          <button onClick={() => switchTab('amazon')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'amazon' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            {t('purchases.amazonPurchases')}
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases.pos}>
          <button onClick={() => switchTab('pos')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'pos' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            {t('purchases.stockPOs')}
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases['internal-pos']}>
          <button onClick={() => switchTab('internal-pos')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'internal-pos' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            {t('purchases.internalPos')}
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases['internal-stock']}>
          <button onClick={() => switchTab('internal-stock')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'internal-stock' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            {t('purchases.internalStock')}
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases['internal-movements']}>
          <button onClick={() => switchTab('internal-movements')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'internal-movements' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            {t('purchases.internalMovements')}
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases.orphans}>
          <button onClick={() => switchTab('orphans')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'orphans' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            <span className="flex items-center gap-1">Huérfanas</span>
          </button>
        </Can>
        <Can cap={TAB_CAP.purchases.suppliers}>
          <button onClick={() => switchTab('suppliers')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'suppliers' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}>
            <span className="flex items-center gap-1"><Truck size={14} /> Proveedores</span>
          </button>
        </Can>
      </div>

      {/* Stats */}
      {stats && tab === 'amazon' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'var(--color-primary)' },
            { label: t('purchases.ordered'), value: stats.ordered, color: 'var(--color-primary)' },
            { label: t('purchases.shipped'), value: stats.shipped, color: 'var(--color-warning)' },
            { label: t('purchases.delivered'), value: stats.delivered, color: 'var(--color-success)' },
            { label: t('purchases.cancelled'), value: stats.cancelled, color: 'var(--color-error)' },
            ...(canMoney ? [
              { label: t('purchases.totalSpend'), value: fmt(stats.total_spend), color: 'var(--color-text)' },
              { label: t('purchases.totalProfit'), value: fmt(stats.total_profit), color: 'var(--color-success)' },
            ] : []),
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {stats && tab === 'pos' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'var(--color-primary)' },
            { label: t('purchases.draft'), value: stats.draft, color: 'var(--color-text-muted)' },
            { label: t('purchases.confirmed'), value: stats.confirmed, color: 'var(--color-primary)' },
            { label: t('purchases.partial'), value: stats.partial, color: 'var(--color-warning)' },
            { label: t('purchases.received'), value: stats.received, color: 'var(--color-success)' },
            ...(canMoney ? [{ label: t('purchases.totalSpend'), value: fmt(stats.total_cost), color: 'var(--color-text)' }] : []),
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {stats && tab === 'internal-pos' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'var(--color-primary)' },
            { label: 'En Hold', value: stats.hold, color: 'var(--color-warning)' },
            { label: 'Procesadas', value: stats.released, color: 'var(--color-success)' },
            { label: 'Devueltas', value: stats.returned, color: '#a855f7' },
            ...(canMoney ? [
              { label: 'Costo Total', value: fmt(stats.total_cost), color: 'var(--color-text)' },
              { label: 'Profit Total', value: fmt(stats.total_profit), color: 'var(--color-success)' },
            ] : []),
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters — only on tabs that need server-side search/status filtering */}
      {(tab === 'amazon' || tab === 'pos' || tab === 'internal-pos') && <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input type="text" placeholder={t('purchases.searchPlaceholder')} defaultValue={search}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]">
          <option value="all">Todos</option>
          {tab === 'amazon' ? (
            <>
              <option value="ordered">{t('purchases.ordered')}</option>
              <option value="shipped">{t('purchases.shipped')}</option>
              <option value="delivered">{t('purchases.delivered')}</option>
              <option value="pending_approval">{t('purchases.pending')}</option>
              <option value="cancelled">{t('purchases.cancelled')}</option>
              <option value="error">{t('purchases.errors')}</option>
            </>
          ) : tab === 'pos' ? (
            <>
              <option value="draft">{t('purchases.draft')}</option>
              <option value="confirmed">{t('purchases.confirmed')}</option>
              <option value="partial_received">{t('purchases.partial')}</option>
              <option value="received">{t('purchases.received')}</option>
              <option value="cancelled">{t('purchases.cancelled')}</option>
            </>
          ) : (
            <>
              <option value="hold">En Hold</option>
              <option value="released">Procesadas</option>
              <option value="returned">Devueltas</option>
              <option value="cancelled">{t('purchases.cancelled')}</option>
            </>
          )}
        </select>
      </div>}

      {/* Table — only on tabs that use the unified data/pagination flow */}
      {(tab === 'amazon' || tab === 'pos' || tab === 'internal-pos') && <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="animate-pulse p-4 space-y-3">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-14 bg-[var(--color-border-light)] rounded" />)}
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <ShoppingCart size={48} className="opacity-20 mb-3" />
            <p>{t('purchases.noData')}</p>
          </div>
        ) : tab === 'amazon' ? (
          isMobile ? (
            <div className="space-y-2 p-2">
              {data.map((p: any) => (
                <AmazonPurchaseCard key={p.id} p={p} fmt={fmt} fmtDate={fmtDate} sellerId={sellerId} onClick={() => setSelectedItem(p)} />
              ))}
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5 w-12" />
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.asin')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.cost')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.profit')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.status')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.tracking')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.mlOrder')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.date')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p: any) => (
                  <tr key={p.id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] cursor-pointer"
                    onClick={() => setSelectedItem(p)}>
                    <td className="px-3 py-2">
                      <div className="w-10 h-10 rounded overflow-hidden bg-[var(--color-border-light)]">
                        {p.product_image ? <img src={p.product_image} alt="" className="w-full h-full object-contain" /> :
                          <Package size={16} className="w-full h-full p-2.5 text-[var(--color-text-muted)]" />}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm"><EntityLink type="asin" value={p.asin} sellerId={sellerId} /></div>
                      <div className="text-xs text-[var(--color-text-muted)]">{p.product_title || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-mono">{fmt(p.total_cost)}</td>
                    <td className={`px-3 py-2 text-right text-sm font-mono ${(p.gross_profit ?? 0) >= 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>
                      {fmt(p.gross_profit)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge status={AMZ_STATUS_MAP[p.amazon_status] || 'unknown'} label={p.amazon_status} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <PurchaseTracker
                        events={p.events || []}
                        currentStatus={p.amazon_status}
                        compact
                        trackingNumber={p.tracking_number}
                        carrier={p.carrier}
                        unitCost={canMoney ? p.total_cost : undefined}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm">{p.ml_order_id ? <EntityLink type="ml_order" value={p.ml_order_id} sellerId={sellerId} /> : '—'}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        ) : tab === 'pos' ? (
          /* POs table */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.poNumber')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Proveedor</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.status')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.fulfillment')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.items')}</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.cost')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('purchases.tracking')}</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">{t('common.date')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((po: any) => (
                  <tr key={po.id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] cursor-pointer"
                    onClick={() => setSelectedItem(po)}>
                    <td className="px-3 py-2 text-sm font-medium">{po.po_number ? <EntityLink type="po_number" value={po.po_number} sellerId={sellerId} /> : `PO-${po.id}`}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">{po.supplier_name || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <StatusBadge status={PO_STATUS_MAP[po.status] || 'unknown'} label={po.status} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">{po.fulfillment_status || '—'}</td>
                    <td className="px-3 py-2 text-right text-sm">{po.item_count || 0}</td>
                    <td className="px-3 py-2 text-right text-sm font-mono">
                      {!canMoney ? '—' : po.total_cost > 0 ? fmt(po.total_cost) : po.estimated_cost > 0 ? <span className="text-[var(--color-text-muted)]" title="Estimated">~{fmt(po.estimated_cost)}</span> : '$0.00'}
                    </td>
                    <td className="px-3 py-2 text-xs">{po.tracking_number ? <EntityLink type="tracking" value={po.tracking_number} sellerId={sellerId} /> : '—'}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">{fmtDate(po.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === 'internal-pos' ? (
          /* Internal purchases table */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Producto</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">SKU/ASIN</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Qty</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Costo</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Profit</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Estado</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">ML Orden</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Fecha</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase px-3 py-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.map((ip: any) => (
                  <tr key={ip.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] transition-colors">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {ip.image_url && <img src={ip.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                        <span className="text-sm text-[var(--color-text)]">{ip.product_title || '-'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-[var(--color-text-muted)]">{ip.internal_sku || ip.asin || '-'}</td>
                    <td className="px-3 py-2.5 text-center text-sm text-[var(--color-text)]">{ip.quantity}</td>
                    <td className="px-3 py-2.5 text-right text-sm text-[var(--color-text)]">{canMoney ? `$${(ip.total_cost ?? 0).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-sm">
                      {!canMoney ? '—' : ip.gross_profit != null ? (
                        <span className={ip.gross_profit >= 0 ? 'text-green-600' : 'text-red-500'}>${ip.gross_profit.toFixed(2)}</span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <StatusBadge status={
                        ip.status === 'hold' ? 'warning' : ip.status === 'released' ? 'ok' : ip.status === 'returned' ? 'info' : 'error'
                      } label={ip.status === 'hold' ? 'Hold' : ip.status === 'released' ? 'Procesada' : ip.status === 'returned' ? 'Devuelta' : 'Cancelada'} />
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[var(--color-text-muted)]">{ip.ml_order_id || '-'}</td>
                    <td className="px-3 py-2.5 text-xs text-[var(--color-text-muted)]">{fmtDate(ip.created_at)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Can cap="purchases.operate_pos">
                        <div className="flex items-center justify-center gap-1">
                          {ip.status === 'hold' && (
                            <button onClick={async () => {
                              await api.post(`/internal-stock/purchases/${ip.id}/release`, {});
                              loadData();
                            }} className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700">Release</button>
                          )}
                          {(ip.status === 'hold' || ip.status === 'released') && (
                            <button onClick={async () => {
                              await api.post(`/internal-stock/purchases/${ip.id}/return`, {});
                              loadData();
                            }} className="px-2 py-1 text-xs rounded bg-purple-600 text-white hover:bg-purple-700">Devolver</button>
                          )}
                        </div>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-[var(--color-border)]">
          <Pagination
            page={pag.page}
            totalPages={pag.totalPages}
            total={pag.total}
            perPage={pag.perPage}
            onPageChange={pag.setPage}
            onPerPageChange={pag.setPerPage}
          />
        </div>
      </div>}

      {/* Tabs with their own self-contained UI — rendered outside the unified table */}
      {tab === 'internal-stock' && <InternalStockTab sellerId={sellerId} />}
      {tab === 'internal-movements' && <InternalMovementsTab sellerId={sellerId} />}
      {tab === 'orphans' && <OrphansTab />}
      {tab === 'suppliers' && <SuppliersPanel />}

      {/* Detail panel */}
      {selectedItem && (tab === 'amazon' || tab === 'pos' || tab === 'internal-pos') && (
        <PurchaseDetailPanel item={selectedItem} tab={tab} sellerId={sellerId}
          onClose={() => setSelectedItem(null)}
          onUpdate={() => { loadData(); }} />
      )}

      {/* Create PO modal */}
      {showCreateModal && (
        <CreatePOModal
          sellerId={sellerId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); if (tab !== 'pos') switchTab('pos'); loadData(); }}
        />
      )}
    </div>
  );
}

function PurchaseDetailPanel({ item, tab, sellerId, onClose, onUpdate }: { item: any; tab: Tab; sellerId: number; onClose: () => void; onUpdate: () => void }) {
  const { t } = useTranslation();
  const [poDetail, setPODetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [actionError, setActionError] = useState('');
  const [receiveMode, setReceiveMode] = useState(false);
  const [receiveQtys, setReceiveQtys] = useState<Record<number, number>>({});
  const can = useCan();
  const canMoney = can('global.view_money');

  const fmt = (v: any) => (canMoney && v != null ? `$${Number(v).toFixed(2)}` : '—');
  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleString() : '—';

  // Load PO detail with items when in POS tab
  useEffect(() => {
    if (tab === 'pos' && item.id) {
      setLoadingDetail(true);
      api.get<any>(`/purchases/pos/${item.id}?seller_id=${sellerId}`).then(res => {
        if (res.success && res.data) setPODetail(res.data);
        setLoadingDetail(false);
      });
    }
  }, [tab, item.id, sellerId]);

  const handleDeleteAmazon = async () => {
    if (!confirm('Eliminar esta compra? La orden quedara libre para re-comprar.')) return;
    setActionLoading('delete');
    setActionError('');
    const res = await api.delete(`/purchases/amazon/${item.id}?seller_id=${sellerId}`);
    setActionLoading('');
    if (res.success) { onUpdate(); onClose(); }
    else setActionError((res as any).error || 'Error');
  };

  const handleConfirm = async () => {
    if (!confirm(t('purchases.actions.confirmQuestion'))) return;
    setActionLoading('confirm');
    setActionError('');
    const res = await api.post(`/purchases/pos/${item.id}/confirm`, { seller_id: sellerId });
    setActionLoading('');
    if (res.success) { onUpdate(); onClose(); }
    else setActionError((res as any).error || 'Error');
  };

  const handleCancel = async () => {
    if (!confirm(t('purchases.actions.cancelQuestion'))) return;
    setActionLoading('cancel');
    setActionError('');
    const res = await api.delete(`/purchases/pos/${item.id}?seller_id=${sellerId}`);
    setActionLoading('');
    if (res.success) { onUpdate(); onClose(); }
    else setActionError((res as any).error || 'Error');
  };

  const handleReceive = async () => {
    const items = Object.entries(receiveQtys)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ po_item_id: parseInt(id), qty_received: qty }));
    if (items.length === 0) return;

    setActionLoading('receive');
    setActionError('');
    const res = await api.post(`/purchases/pos/${item.id}/receive`, { seller_id: sellerId, items });
    setActionLoading('');
    if (res.success) {
      setReceiveMode(false);
      setReceiveQtys({});
      onUpdate();
      // Refresh detail
      const detRes = await api.get<any>(`/purchases/pos/${item.id}?seller_id=${sellerId}`);
      if (detRes.success && detRes.data) setPODetail(detRes.data);
    } else {
      setActionError((res as any).error || 'Error');
    }
  };

  const startReceiveAll = () => {
    if (!poDetail?.items) return;
    const qtys: Record<number, number> = {};
    for (const it of poDetail.items) {
      const remaining = (it.qty_ordered || 0) - (it.qty_received || 0);
      if (remaining > 0) qtys[it.id] = remaining;
    }
    setReceiveQtys(qtys);
    setReceiveMode(true);
  };

  const po = poDetail || item;
  const canOperatePos = can('purchases.operate_pos');
  const canReceive = ['confirmed', 'partial_received'].includes(po.status) && canOperatePos;
  const canConfirm = po.status === 'draft' && canOperatePos;
  // Borrador → borra la orden (llave de borrado); confirmada → la cancela (operar compras).
  const canCancel = ['draft', 'confirmed'].includes(po.status)
    && (po.status === 'draft' ? can('purchases.delete') : canOperatePos);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-[var(--color-surface)] shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[var(--color-text)]">
            {tab === 'amazon' ? t('purchases.detail') : t('purchases.poDetail')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {tab === 'amazon' ? (
            <>
              {/* Amazon purchase detail */}
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--color-border-light)] border border-[var(--color-border)]">
                  {item.product_image ? <img src={item.product_image} alt="" className="w-full h-full object-contain p-1" /> :
                    <Package size={24} className="w-full h-full p-4 text-[var(--color-text-muted)]" />}
                </div>
                <div>
                  <div className="text-sm font-semibold"><EntityLink type="asin" value={item.asin} sellerId={sellerId} /></div>
                  <div className="text-xs text-[var(--color-text-muted)]">{item.product_title || '—'}</div>
                  <div className="mt-1"><StatusBadge status={AMZ_STATUS_MAP[item.amazon_status] || 'unknown'} label={item.amazon_status} size="sm" /></div>
                </div>
              </div>

              {/* Purchase lifecycle tracker */}
              <div className="p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                <PurchaseTracker
                  events={item.events || []}
                  currentStatus={item.amazon_status}
                  trackingNumber={item.tracking_number}
                  carrier={item.carrier}
                  unitCost={canMoney ? item.total_cost : undefined}
                  deliveryDate={item.actual_delivery_date || item.estimated_delivery_end}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Can cap="global.view_money">
                  <InfoCard label={t('purchases.cost')} value={fmt(item.total_cost)} />
                  <InfoCard label={t('purchases.profit')} value={fmt(item.gross_profit)}
                    valueColor={(item.gross_profit ?? 0) >= 0 ? 'var(--color-success)' : 'var(--color-error)'} />
                  <InfoCard label={t('purchases.margin')} value={item.profit_margin ? `${Number(item.profit_margin).toFixed(1)}%` : '—'} />
                </Can>
                <InfoCard label="Qty" value={String(item.quantity || 1)} />
                <div className="px-3 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">{t('purchases.mlOrder')}</div>
                  <div className="text-sm font-semibold mt-0.5">{item.ml_order_id ? <EntityLink type="ml_order" value={item.ml_order_id} sellerId={sellerId} /> : '—'}</div>
                </div>
                <InfoCard label={t('purchases.buyer')} value={item.buyer_nickname || '—'} />
                <InfoCard label={t('purchases.linkSource')} value={item.link_source || '—'} />
                {item.purchase_account_name && (
                  <InfoCard label="Cuenta Amazon" value={`${item.purchase_account_name} (rotación por límite)`} />
                )}
                <div className="px-3 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Amazon Order</div>
                  <div className="text-sm font-semibold mt-0.5">{item.amazon_order_id ? <EntityLink type="amazon_order" value={item.amazon_order_id} sellerId={sellerId} /> : '—'}</div>
                </div>
              </div>

              {item.tracking_number && (
                <div className="p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck size={14} className="text-[var(--color-primary)]" />
                    <span className="text-xs font-semibold uppercase text-[var(--color-text-muted)]">{t('purchases.tracking')}</span>
                  </div>
                  <div className="text-sm"><EntityLink type="tracking" value={item.tracking_number} sellerId={sellerId} /></div>
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    {item.carrier && <span>{item.carrier} · </span>}
                    {item.ship_date && <span>Enviado: {fmtDate(item.ship_date)}</span>}
                  </div>
                </div>
              )}

              {item.error_message && (
                <div className="p-3 rounded-lg bg-[var(--color-error-light)] border border-[var(--color-error)] text-sm text-[var(--color-error)]">
                  {item.error_message}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-xs">
                <InfoCard label={t('catalog.created')} value={fmtDate(item.created_at)} />
                <InfoCard label={t('purchases.delivery')} value={fmtDate(item.actual_delivery_date || item.estimated_delivery_end)} />
              </div>

              {['pending_approval', 'safeguard_hold', 'error', 'cancelled'].includes(item.amazon_status) && can('purchases.delete') && (
                <button
                  onClick={handleDeleteAmazon}
                  disabled={actionLoading === 'delete'}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--color-error)] text-[var(--color-error)] hover:bg-[var(--color-error-light)] transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {actionLoading === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Eliminar y liberar para re-compra
                </button>
              )}

              {actionError && (
                <div className="p-2 rounded-lg bg-[var(--color-error-light)] text-sm text-[var(--color-error)]">{actionError}</div>
              )}
            </>
          ) : (
            <>
              {/* PO detail */}
              <div>
                <div className="text-lg font-bold">{po.po_number ? <EntityLink type="po_number" value={po.po_number} sellerId={sellerId} /> : `PO-${po.id}`}</div>
                <div className="flex gap-2 mt-1">
                  <StatusBadge status={PO_STATUS_MAP[po.status] || 'unknown'} label={po.status} size="sm" />
                  {po.fulfillment_status && (
                    <span className="text-xs text-[var(--color-text-muted)] self-center">{po.fulfillment_status}</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <InfoCard label={t('purchases.items')} value={String(po.item_count || po.items?.length || 0)} />
                <InfoCard label={t('purchases.cost')} value={!canMoney ? '—' : po.total_cost > 0 ? fmt(po.total_cost) : po.estimated_cost > 0 ? `~${fmt(po.estimated_cost)}` : '$0.00'} />
                <InfoCard label={t('purchases.qtyOrdered')} value={String(po.total_qty_ordered || po.total_items || 0)} />
                <InfoCard label={t('purchases.qtyReceived')} value={String(po.total_qty_received || 0)} />
              </div>

              {po.tracking_number && (
                <div className="p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck size={14} className="text-[var(--color-primary)]" />
                    <span className="text-xs font-semibold uppercase text-[var(--color-text-muted)]">{t('purchases.tracking')}</span>
                  </div>
                  <div className="text-sm"><EntityLink type="tracking" value={po.tracking_number} sellerId={sellerId} /></div>
                  {po.carrier_name && <div className="text-xs text-[var(--color-text-muted)] mt-1">{po.carrier_name}</div>}
                </div>
              )}

              {po.notes && (
                <div className="p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">{t('purchases.create.notes')}</div>
                  <div className="text-sm text-[var(--color-text)]">{po.notes}</div>
                </div>
              )}

              {/* PO Items */}
              {loadingDetail ? (
                <div className="animate-pulse space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-12 bg-[var(--color-border-light)] rounded" />)}
                </div>
              ) : poDetail?.items && poDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{t('purchases.create.items')}</h3>
                  <div className="space-y-2">
                    {poDetail.items.map((it: any) => {
                      const remaining = (it.qty_ordered || 0) - (it.qty_received || 0);
                      return (
                        <div key={it.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                          <div className="w-10 h-10 rounded overflow-hidden bg-[var(--color-border-light)] flex-shrink-0">
                            {it.product_image ? <img src={it.product_image} alt="" className="w-full h-full object-contain" /> :
                              <Package size={14} className="w-full h-full p-2.5 text-[var(--color-text-muted)]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-[var(--color-primary)]">{it.asin}</span>
                              {it.unit_cost > 0 && canMoney && <span className="text-xs font-mono text-[var(--color-text-muted)]">${Number(it.unit_cost).toFixed(2)}</span>}
                            </div>
                            <div className="text-xs text-[var(--color-text-muted)]">{it.product_title || '—'}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs">{t('purchases.qtyOrdered')}: {it.qty_ordered}</span>
                              <span className="text-xs text-[var(--color-success)]">{t('purchases.qtyReceived')}: {it.qty_received || 0}</span>
                              {remaining > 0 && <span className="text-xs text-[var(--color-warning)]">{t('purchases.actions.pending')}: {remaining}</span>}
                            </div>
                          </div>
                          {receiveMode && remaining > 0 && (
                            <input type="number" min={0} max={remaining}
                              value={receiveQtys[it.id] ?? 0}
                              onChange={e => setReceiveQtys({ ...receiveQtys, [it.id]: Math.min(remaining, Math.max(0, parseInt(e.target.value) || 0)) })}
                              className="w-16 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-center" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              {actionError && (
                <div className="p-3 rounded-lg bg-[var(--color-error-light)] text-[var(--color-error)] text-sm">{actionError}</div>
              )}

              {(canConfirm || canReceive || canCancel || receiveMode) && <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--color-border)]">
                {canConfirm && (
                  <button onClick={handleConfirm} disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] disabled:opacity-50">
                    {actionLoading === 'confirm' && <Loader2 size={14} className="animate-spin" />}
                    {t('purchases.actions.confirm')}
                  </button>
                )}
                {canReceive && !receiveMode && (
                  <button onClick={startReceiveAll}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-success)] text-white text-sm font-medium hover:opacity-90">
                    {t('purchases.actions.receiveAll')}
                  </button>
                )}
                {receiveMode && (
                  <>
                    <button onClick={handleReceive} disabled={!!actionLoading || Object.values(receiveQtys).every(q => q === 0)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-success)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">
                      {actionLoading === 'receive' && <Loader2 size={14} className="animate-spin" />}
                      {t('purchases.actions.confirmReceive')}
                    </button>
                    <button onClick={() => { setReceiveMode(false); setReceiveQtys({}); }}
                      className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]">
                      {t('common.cancel')}
                    </button>
                  </>
                )}
                {canCancel && !receiveMode && (
                  <button onClick={handleCancel} disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[var(--color-error)] text-[var(--color-error)] text-sm font-medium hover:bg-[var(--color-error-light)] disabled:opacity-50 ml-auto">
                    {actionLoading === 'cancel' && <Loader2 size={14} className="animate-spin" />}
                    {po.status === 'draft' ? t('common.delete') : t('purchases.actions.cancel')}
                  </button>
                )}
              </div>}

              <div className="grid grid-cols-2 gap-3 text-xs">
                <InfoCard label={t('catalog.created')} value={fmtDate(po.created_at)} />
                <InfoCard label={t('purchases.delivery')} value={fmtDate(po.actual_delivery || po.estimated_delivery)} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

interface POItemRow {
  asin: string;
  title: string;
  qty_ordered: number;
  unit_cost: number;
  image_url?: string;
}

function CreatePOModal({ sellerId, onClose, onCreated }: { sellerId: number; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  // El costo histórico del buscador es dato guardado: solo con la llave del dinero.
  const canMoney = can('global.view_money');
  const [items, setItems] = useState<POItemRow[]>([{ asin: '', title: '', qty_ordered: 1, unit_cost: 0 }]);
  const [notes, setNotes] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchingIdx, setSearchingIdx] = useState<number | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [supplierId, setSupplierId] = useState<number | ''>('');

  useEffect(() => {
    api.get<any[]>('/suppliers').then(res => {
      if (res.success && res.data) setSuppliers(res.data);
    });
  }, []);

  const addItem = () => {
    setItems([...items, { asin: '', title: '', qty_ordered: 1, unit_cost: 0 }]);
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof POItemRow, value: string | number) => {
    const updated = [...items];
    (updated[idx] as any)[field] = value;
    setItems(updated);
  };

  const handleAsinSearch = (idx: number, val: string) => {
    updateItem(idx, 'asin', val.toUpperCase());
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.length < 2) { setSearchResults([]); setSearchingIdx(null); return; }

    setSearchingIdx(idx);
    searchTimeoutRef.current = setTimeout(async () => {
      const res = await api.get<any[]>(`/purchases/products/search?q=${encodeURIComponent(val)}&seller_id=${sellerId}`);
      if (res.success && res.data) {
        setSearchResults(res.data);
        setSearchingIdx(idx);
      }
    }, 300);
  };

  const selectProduct = (idx: number, product: any) => {
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      asin: product.asin,
      title: product.title || '',
      // Sin la llave del dinero el costo histórico NO se precarga en el campo visible.
      unit_cost: canMoney ? (product.last_cost || 0) : 0,
      image_url: product.image_url,
    };
    setItems(updated);
    setSearchResults([]);
    setSearchingIdx(null);
  };

  const totalCost = items.reduce((sum, it) => sum + it.qty_ordered * it.unit_cost, 0);
  const totalItems = items.reduce((sum, it) => sum + it.qty_ordered, 0);

  const handleSave = async () => {
    const validItems = items.filter(it => it.asin.trim());
    if (validItems.length === 0) {
      setError(t('purchases.create.errorNoItems'));
      return;
    }

    setSaving(true);
    setError('');

    const res = await api.post('/purchases/pos', {
      seller_id: sellerId,
      supplier_id: supplierId || null,
      items: validItems,
      notes: notes || null,
      tracking_number: trackingNumber || null,
      auto_confirm: autoConfirm,
    });

    setSaving(false);

    if (res.success) {
      onCreated();
    } else {
      setError((res as any).error || 'Error al crear la orden');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[var(--color-surface)] rounded-xl shadow-2xl border border-[var(--color-border)] w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
            <h2 className="text-xl font-bold text-[var(--color-text)]">{t('purchases.newPO')}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
              <X size={20} className="text-[var(--color-text-muted)]" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Supplier selector */}
            <div>
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-1 block">Proveedor</label>
              <select value={supplierId} onChange={e => setSupplierId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]">
                <option value="">— Sin proveedor —</option>
                {suppliers.filter((s: any) => s.status === 'active').map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}{s.type ? ` (${s.type})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--color-text)]">{t('purchases.create.items')}</h3>
                <button onClick={addItem}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:opacity-80 transition-opacity">
                  <Plus size={14} /> {t('purchases.create.addItem')}
                </button>
              </div>

              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
                    {/* Thumbnail */}
                    <div className="w-12 h-12 rounded overflow-hidden bg-[var(--color-border-light)] flex-shrink-0 mt-1">
                      {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-contain" /> :
                        <Package size={16} className="w-full h-full p-3 text-[var(--color-text-muted)]" />}
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        {/* ASIN input with autocomplete */}
                        <div className="relative flex-1 max-w-[180px]">
                          <input type="text" placeholder="ASIN" value={item.asin}
                            onChange={e => handleAsinSearch(idx, e.target.value)}
                            onBlur={() => setTimeout(() => { if (searchingIdx === idx) { setSearchResults([]); setSearchingIdx(null); } }, 200)}
                            className="w-full px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm font-mono text-[var(--color-text)]" />
                          {searchingIdx === idx && searchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                              {searchResults.map((p: any) => (
                                <button key={p.asin} type="button"
                                  onMouseDown={e => { e.preventDefault(); selectProduct(idx, p); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-surface-hover)] text-sm">
                                  <span className="font-mono text-[var(--color-primary)] text-xs">{p.asin}</span>
                                  <span className="text-[var(--color-text-muted)] text-xs truncate flex-1">{p.title || '—'}</span>
                                  {p.last_cost > 0 && canMoney && <span className="text-xs font-mono">${p.last_cost.toFixed(2)}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Title */}
                        <input type="text" placeholder={t('purchases.create.titlePlaceholder')} value={item.title}
                          onChange={e => updateItem(idx, 'title', e.target.value)}
                          className="flex-1 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]" />
                      </div>
                      <div className="flex gap-2 items-center">
                        <div className="flex items-center gap-1">
                          <label className="text-xs text-[var(--color-text-muted)]">Qty</label>
                          <input type="number" min={1} value={item.qty_ordered}
                            onChange={e => updateItem(idx, 'qty_ordered', Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-16 px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-center text-[var(--color-text)]" />
                        </div>
                        <div className="flex items-center gap-1">
                          <label className="text-xs text-[var(--color-text-muted)]">{t('purchases.cost')}</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)]">$</span>
                            <input type="number" step="0.01" min={0} value={item.unit_cost || ''}
                              onChange={e => updateItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                              className="w-24 pl-5 pr-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]" />
                          </div>
                        </div>
                        <span className="text-xs text-[var(--color-text-muted)] ml-auto">
                          = ${(item.qty_ordered * item.unit_cost).toFixed(2)}
                        </span>
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)}
                            className="p-1 rounded hover:bg-[var(--color-error-light)] text-[var(--color-error)] transition-colors">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tracking + Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[var(--color-text-muted)] mb-1 block">{t('purchases.tracking')}</label>
                <input type="text" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)}
                  placeholder="1Z999AA10123..."
                  className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm font-mono text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--color-text-muted)] mb-1 block">{t('purchases.create.notes')}</label>
                <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder={t('purchases.create.notesPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]" />
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={autoConfirm} onChange={e => setAutoConfirm(e.target.checked)}
                  className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)]" />
                <span className="text-sm text-[var(--color-text)]">{t('purchases.create.autoConfirm')}</span>
              </label>
              <span className="text-xs text-[var(--color-text-muted)]">
                {autoConfirm ? t('purchases.create.autoConfirmHint') : t('purchases.create.draftHint')}
              </span>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-[var(--color-error-light)] text-[var(--color-error)] text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--color-border)]">
            <div className="text-sm text-[var(--color-text-muted)]">
              {totalItems} {t('purchases.items').toLowerCase()} · <span className="font-mono font-semibold text-[var(--color-text)]">${totalCost.toFixed(2)}</span>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose}
                className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors">
                {t('common.cancel')}
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50">
                {saving && <Loader2 size={16} className="animate-spin" />}
                {t('purchases.create.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoCard({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]">
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">{label}</div>
      <div className="text-sm font-semibold mt-0.5" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
    </div>
  );
}
